import React, {
	type ReactNode,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { loadEntryPoint } from 'react-relay';
import { mergeRefs } from 'use-callback-ref';

import { Popup } from '@atlaskit/popup/popup';
import type { ContentProps, PopupProps, TriggerProps } from '@atlaskit/popup/types';
import Spinner from '@atlaskit/spinner/spinner';
import type {
	AnyEntryPoint,
	EntryPointRef,
	GetRuntimePropsFromEntryPoint,
	ParamsOfEntryPoint,
	RuntimePropsOfEntryPoint,
} from '@atlassian/entry-point-types';
import {
	type ErrorCallback,
	type ErrorFallback,
	InternalEntryPointContainer,
} from '@atlassian/internal-entry-point-container';
import type { JSResourceReference } from '@atlassian/react-async';
import { useRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import { useHoverTriggerRef } from './useHoverTriggerRef';
import { usePreloadRef } from './usePreloadRef';
import { usePressableTriggerRef } from './usePressableTriggerRef';

export type TriggerMode = 'hover' | 'click' | Array<'hover' | 'click'>;

const emptyEntryPointParams = {} as ParamsOfEntryPoint<AnyEntryPoint>;
const emptyEntryPointProps = {} as RuntimePropsOfEntryPoint<AnyEntryPoint>;

type UnionOmit<T, K extends string | number | symbol> = T extends unknown ? Omit<T, K> : never;

type CustomPopupProps = UnionOmit<PopupProps, 'isOpen' | 'content'>;
type EntryPointPropsType<TEntryPoint> = GetRuntimePropsFromEntryPoint<TEntryPoint>;

const FallbackComponent = ({
	fallback,
	onUnmount,
}: {
	fallback?: ReactNode;
	onUnmount: () => void;
}) => {
	useEffect(() => {
		return () => onUnmount();
	}, [onUnmount]);

	return <>{fallback || <Spinner />}</>;
};

export type PopupTriggerWithHoverProps<TEntryPoint> = {
	entryPoint: TEntryPoint;
	entryPointParams?: ParamsOfEntryPoint<TEntryPoint>;
	entryPointProps?: Omit<EntryPointPropsType<TEntryPoint>, 'onClose' | 'onContentResized'> & {
		onClose?: () => void;
		onContentResized?: ContentProps['update'];
	};
	errorFallback?: ErrorFallback;
	fallback?: ReactNode;
	forcedReportErrorUFO?: boolean;
	onError?: ErrorCallback;
	onOpen?: (method: 'hover' | 'click') => void;
	trigger: (triggerProps: TriggerProps) => ReactNode;
	/**
	 * How the popup should be triggered. Defaults to `'click'` when omitted.
	 */
	triggerMode?: TriggerMode;
} & CustomPopupProps;

export function PopupTriggerWithHover<TEntryPoint extends AnyEntryPoint>({
	trigger,
	triggerMode = 'click',
	entryPoint,
	entryPointParams,
	entryPointProps,
	fallback,
	errorFallback,
	onError,
	forcedReportErrorUFO,
	onOpen,
	onClose,
	...popupProps
}: PopupTriggerWithHoverProps<TEntryPoint>): React.JSX.Element {
	const environmentProvider = useRelayEnvironmentProvider();

	const id = useMemo((): string => {
		return (entryPoint.root as JSResourceReference<unknown>).getModuleName() || 'unknown';
	}, [entryPoint]);

	const [isOpen, setIsOpen] = useState(false);
	const [content, setContent] = useState<ReactNode | null>(null);

	const updateRef = useRef<(() => void) | null>(null);
	const openedByRef = useRef<'hover' | 'click' | null>(null);
	const isOpenRef = useRef(isOpen);
	const triggers = Array.isArray(triggerMode) ? triggerMode : [triggerMode];

	useLayoutEffect(() => {
		isOpenRef.current = isOpen;
	}, [isOpen]);

	const handleOpen = useCallback(
		(method: 'hover' | 'click' | null) => {
			setIsOpen(true);
			if (method) {
				onOpen?.(method);
			}
		},
		[onOpen],
	);

	const handleClose = useCallback(
		(
			event?: Event | React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element> | null,
			currentLevel?: any,
		) => {
			setIsOpen(false);
			onClose?.(event ?? null, currentLevel);
		},
		[onClose],
	);

	const load = useCallback(
		() =>
			loadEntryPoint<TEntryPoint>(
				environmentProvider,
				entryPoint,
				entryPointParams ?? emptyEntryPointParams,
			),
		[entryPoint, entryPointParams, environmentProvider],
	);

	const onLoad = useCallback(
		({ reference: entryPointReference }: { reference: EntryPointRef<TEntryPoint> }) => {
			if (isOpenRef.current) {
				return;
			}

			handleOpen(openedByRef.current);

			setContent(
				<InternalEntryPointContainer
					id={id}
					entryPointReference={entryPointReference}
					fallback={
						<FallbackComponent fallback={fallback} onUnmount={() => updateRef.current?.()} />
					}
					errorFallback={errorFallback}
					runtimeProps={
						{
							...(entryPointProps ?? emptyEntryPointProps),
							onContentResized: () => {
								updateRef.current?.();
							},
							onClose: handleClose,
						} as unknown as EntryPointPropsType<TEntryPoint>
					}
					onError={onError}
					forcedError={forcedReportErrorUFO}
				/>,
			);
		},
		[
			setContent,
			id,
			entryPointProps,
			fallback,
			forcedReportErrorUFO,
			errorFallback,
			handleOpen,
			onError,
			handleClose,
		],
	);

	const preloadRef = usePreloadRef<EntryPointRef<TEntryPoint>>({
		load,
		onLoad,
	});

	const pressableRef = usePressableTriggerRef({
		onOpen: preloadRef.loadAndOpen,
		onClose: handleClose,
		isOpen,
		isDisabled: !triggers.includes('click'),
		openedByRef,
	});

	const { triggerRef: hoverTriggerRef, contentRef: hoverContentRef } = useHoverTriggerRef({
		onOpen: preloadRef.loadAndOpen,
		onClose: handleClose,
		isOpen,
		isDisabled: !triggers.includes('hover'),
		openedByRef,
	});

	useEffect(() => {
		if (!isOpen) {
			openedByRef.current = null;
		}
	}, [isOpen]);

	const popupTrigger = useCallback(
		(triggerProps: TriggerProps) => {
			const mergedRef = mergeRefs([preloadRef, pressableRef, hoverTriggerRef, triggerProps.ref]);
			return trigger({ ...triggerProps, ref: mergedRef });
		},
		[trigger, preloadRef, pressableRef, hoverTriggerRef],
	);

	const popupContent = useCallback(
		({ update }: ContentProps) => {
			updateRef.current = update;
			return (
				<div ref={hoverContentRef} role="none">
					{content}
				</div>
			);
		},
		[content, hoverContentRef],
	);

	return (
		<Popup
			{...popupProps}
			isOpen={isOpen}
			onClose={handleClose}
			trigger={popupTrigger}
			content={popupContent}
		/>
	);
}
